import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CalendarsFilters } from '@/components/Calendars/CalendarsFilters'
import { CalendarCard } from '@/components/Calendars/CalendarCard'
import { CreateCalendarDialog } from '@/components/Calendars/CreateCalendarDialog'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Pagination } from '@/components/common/Pagination'
import { useCalendarsList, useDeleteCalendar } from '@/hooks/useCalendars'
import { toast } from '@/stores/useToastStore'
import type { CalendarHeaderRead } from '@/types'

export default function AdminCalendars() {
  const navigate = useNavigate()

  // Filter state
  const [searchInput, setSearchInput] = useState('')
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(
    undefined
  )
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<CalendarHeaderRead | null>(
    null
  )

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [searchInput, isActiveFilter, pageSize])

  const { data, isLoading, isError } = useCalendarsList({
    search: searchInput,
    isActive: isActiveFilter,
    page,
    pageSize,
  })

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load calendars')
    }
  }, [isError])

  const deleteMutation = useDeleteCalendar()

  const calendars = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleCardClick = useCallback(
    (calendar: CalendarHeaderRead) => {
      navigate(`/admin/calendars/${calendar.id}`)
    },
    [navigate]
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    await deleteMutation.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }, [deleteTarget, deleteMutation])

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Calendar Management
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            Create and manage calendars for your assortment plans
          </p>
        </div>
        <Button className="cursor-pointer" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Calendar
        </Button>
      </div>

      {/* Filters */}
      <CalendarsFilters
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        isActiveFilter={isActiveFilter}
        onIsActiveFilterChange={setIsActiveFilter}
      />

      {/* Calendar List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : calendars.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">
            No calendars found
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Try adjusting your filters or create a new calendar.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {calendars.map(calendar => (
            <CalendarCard
              key={calendar.id}
              calendar={calendar}
              onClick={() => handleCardClick(calendar)}
              onDelete={() => setDeleteTarget(calendar)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      <Pagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {/* Create Dialog */}
      <CreateCalendarDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null)
        }}
        title="Delete Calendar"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
